import clsx from 'clsx';
import type { CollectionState } from '@arcanum-hq/shared-types';

export interface NextFieldGuideProps {
  /**
   * Server-computed collection-state (ADR 0002/0003). Banner reads
   * collectionState.nextGroup. When undefined or null, renders nothing.
   * When collectionState.nextGroup is null (analysis stage), also renders
   * nothing — the strip cleanly disappears in analysis stage.
   */
  collectionState?: CollectionState | null;
  className?: string;
}

/**
 * Toolbar pill that surfaces the agent's "do this next" group recommendation.
 * Reads the server-computed CollectionState carrier (ADR 0002/0003, Phase 5).
 * Renders the group label + count from nextGroup. When nextGroup is null
 * (stage === 'analysis'), the strip cleanly returns null.
 *
 * Outer chrome: VERBATIM existing Intelligence Terminal strip tokens.
 * nextGroup.group is a free-form z.string() — rendered as-is (R6).
 *
 * Click is currently visual only — the user types the value into chat.
 */
export function NextFieldGuide({
  collectionState,
  className,
}: NextFieldGuideProps) {
  if (!collectionState || !collectionState.nextGroup) return null;

  const { nextGroup } = collectionState;
  const count = nextGroup.count;
  const countLabel = count === 1 ? '1 field' : `${count} fields`;

  return (
    <div
      data-testid="next-field-guide"
      data-group={nextGroup.group}
      data-count={nextGroup.count}
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[2px]',
        'border border-accent/15 bg-accent-dim text-accent',
        'font-mono text-micro tracking-[0.3px]',
        className
      )}
    >
      <span className="font-semibold uppercase tracking-[0.4px]">Next:</span>
      <span className="font-semibold">{nextGroup.group}</span>
      <span className="text-text-muted truncate max-w-[260px]">
        — {countLabel}
      </span>
    </div>
  );
}
